/**
 * Chuẩn hoá `role.permissions` từ Strapi về map phẳng
 * `{ [controller]: { [action]: { enabled, policy } } }` và tiện ích tra cứu.
 *
 * Module thuần (không phụ thuộc antd/axios/localStorage) nên dùng được ở cả
 * client (login) lẫn server (route handler /api/admin-bar). Trước đây bản
 * `normalizePermissions` nằm trong `modules/auth/services/apiLogin.ts`
 * (client-only); tách ra đây để server dùng lại đúng một logic.
 */

export interface NormalizedPermissions {
  [controller: string]: {
    [action: string]: {
      enabled: boolean
      policy: string
    }
  }
}

export const normalizePermissions = (permissions: any = {}): NormalizedPermissions => {
  // Shape from /users/me?populate=*:
  // [{ action: "api::post.post.find", ... }, ...]
  if (Array.isArray(permissions)) {
    return permissions.reduce((acc: NormalizedPermissions, item: any) => {
      const actionPath = item?.action
      if (typeof actionPath !== "string") return acc

      const segments = actionPath.split(".")
      if (segments.length < 3) return acc

      const controllerKey = segments[1]
      const actionKey = segments.slice(2).join(".")
      if (!controllerKey || !actionKey) return acc

      if (!acc[controllerKey]) acc[controllerKey] = {}
      acc[controllerKey][actionKey] = {
        enabled: true,
        policy: "",
      }

      return acc
    }, {})
  }

  // Shape from /users-permissions/roles/:id:
  // { "api::post": { controllers: { post: { find: {enabled,...} } } } }
  if (typeof permissions === "object" && permissions !== null) {
    return Object.fromEntries(
      Object.entries(permissions).flatMap(([permissionKey, value]) => {
        const controllers = (value as { controllers?: Record<string, any> })?.controllers

        // Already flattened shape:
        // { post: { find: { enabled, policy } } }
        if (!controllers) {
          if (typeof value !== "object" || value === null) return []
          const normalizedActions = Object.fromEntries(
            Object.entries(value as Record<string, any>).map(([actionKey, actionValue]) => [
              actionKey,
              {
                enabled: !!actionValue?.enabled,
                policy: typeof actionValue?.policy === "string" ? actionValue.policy : "",
              },
            ]),
          )
          return [[permissionKey, normalizedActions]]
        }

        return Object.entries(controllers).map(([controllerKey, controllerValue]) => [
          controllerKey,
          Object.fromEntries(
            Object.entries(controllerValue as { [key: string]: { enabled: boolean; policy: string } }).map(
              ([actionKey, actionValue]) => [
                actionKey,
                {
                  enabled: actionValue.enabled,
                  policy: actionValue.policy,
                },
              ],
            ),
          ),
        ])
      }),
    ) as NormalizedPermissions
  }

  return {}
}

/** Kiểm tra một action dạng "controller.action" (vd "page.update", "post.updateMine"). */
export const hasPerm = (permissions: NormalizedPermissions | null | undefined, action: string): boolean => {
  const [controller, act] = action.split(".")
  if (!controller || !act) return false
  return !!permissions?.[controller]?.[act]?.enabled
}
